import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Radio } from '../../radio';

interface StyledContainerProps {
  disabled?: boolean;
  isSelected?: boolean;
  hasError?: boolean;
}

/**
 * Main container for SpecialRadioSelection component
 */
export const StyledContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'disabled' && prop !== 'isSelected' && prop !== 'hasError',
})<StyledContainerProps & { component?: React.ElementType }>(
  ({ theme, disabled, isSelected, hasError }) => ({
    width: '100%',
    paddingTop: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(5),
    border: `1px solid`,
    borderColor: theme.palette.text.fifth,
    borderRadius: theme.customShape.borderRadius4XL,
    backgroundColor: theme.palette.background.contrast,
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',

    ...(disabled && {
      borderColor: theme.palette.text.fifth,
      backgroundColor: theme.palette.background.contrast,
      opacity: 0.6,
      cursor: 'not-allowed',
      pointerEvents: 'none',
    }),

    ...(isSelected &&
      !disabled && {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.background.blue,
      }),

    ...(hasError && {
      borderColor: theme.palette.error.main,
    }),

    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(3),
      flexDirection: 'row',
      alignItems: 'center',
    },

    '&:hover': {
      ...(!disabled && {
        boxShadow: theme.shadows[4],
      }),
    },
  }),
);

/**
 * Content container with responsive layout
 */
export const StyledContentContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  justifyContent: 'space-between',
  width: '100%',

  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
  },
}));

/**
 * Top row container for radio, icon, and label
 */
export const StyledTopRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

/**
 * Container for right-side icon or credits info
 */
export const StyledIconOrCreditsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  alignSelf: 'flex-end',
}));

interface StyledPriceProps {
  disabled?: boolean;
}

/**
 * Price text styling
 */
export const StyledPrice = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'disabled',
})<StyledPriceProps>(({ disabled }) => ({
  opacity: disabled ? 0.6 : 1,
}));

interface StyledAvailableCreditsProps {
  disabled?: boolean;
}

/**
 * Available credits text styling
 */
export const StyledAvailableCredits = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'disabled',
})<StyledAvailableCreditsProps>(({ theme, disabled }) => ({
  color: theme.palette.text.secondary,
  opacity: disabled ? 0.6 : 1,
}));

interface StyledRadioProps {
  disabled?: boolean;
  hasError?: boolean;
}

/**
 * Radio button styling
 */
export const StyledRadio = styled(Radio, {
  shouldForwardProp: (prop) => prop !== 'disabled' && prop !== 'hasError',
})<StyledRadioProps>(({ theme, disabled, hasError }) => ({
  color: theme.palette.text.second,
  opacity: disabled ? 0.5 : 1,
  ...(hasError && {
    color: theme.palette.error.main,
  }),
}));
